import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button, Card, CardContent, cn } from '@saas-pages/shared/ui'
import {
  startToolOperation,
  trackToolRejection,
  type ToolAnalyticsContext,
  type ToolOperation,
} from '@saas-pages/shared/public/runtime'
import { useLocale } from '@/shared/i18n'
import {
  FilePreflightError,
  preflightRasterFile,
  verifyRasterDecode,
  type ImageMeta,
} from '@/shared/image/filePreflight'
import {
  containedImageBounds,
  coverPreviewStyle,
  pointFromRenderedImage,
  type FocalPoint,
  type RenderedImageBounds,
} from '@/shared/image/cropGeometry'
import {
  SOCIAL_RESIZE_PRESETS,
  socialResizePreset,
  type SocialResizePreset,
  type SocialResizePresetId,
} from '@/features/social-media-resize/model/presets'
import {
  SOCIAL_RESIZE_LIMITS,
  SocialResizeLimitError,
  buildOutputJobs,
  clampOutputPan,
  outputFilename,
  validateSocialResizeSelection,
} from '@/features/social-media-resize/model/socialResize'
import {
  runSocialResizeWorker,
  type SocialResizeWorkerOwner,
} from '@/features/social-media-resize/model/worker'
import { ToolHero } from '@/shared/ui/ToolHero'

interface SourceAsset {
  id: string
  file: File
  url: string
  meta: ImageMeta
  focal: FocalPoint
}

type OutputState =
  | { status: 'pending' | 'processing' }
  | { status: 'error'; message: string }
  | { status: 'ready'; blob: Blob; url: string; filename: string }

interface OutputRecord {
  id: string
  sourceId: string
  presetId: SocialResizePresetId
  focal: FocalPoint
  state: OutputState
}

interface ZipResult {
  blob: Blob
  url: string
}

const ANALYTICS: ToolAnalyticsContext = {
  app: 'imgify',
  domain: 'imgify.ru',
  route: '/razmer-foto-dlya-socsetey',
  utility: 'social-media-resize',
  processingMode: 'local',
  inputMethod: 'upload',
}

const PREFLIGHT_LIMITS = {
  maxFileBytes: SOCIAL_RESIZE_LIMITS.maxFileBytes,
  maxImagePixels: SOCIAL_RESIZE_LIMITS.maxSourcePixels,
  maxLongestSide: SOCIAL_RESIZE_LIMITS.maxLongestSide,
} as const

const DEFAULT_PRESETS: SocialResizePresetId[] = [
  'vk-square-post',
  'telegram-story',
  'youtube-thumbnail',
]

const COPY = {
  ru: {
    eyebrow: 'Размеры для соцсетей',
    title: 'Подготовить фото для соцсетей онлайн',
    description:
      'Добавьте до четырёх изображений, проверьте фокус для шести готовых полотен и скачайте отдельные JPG или один ZIP. Всё остаётся в браузере.',
    steps: ['1. Файлы', '2. Фокус', '3. Размеры', '4. Экспорт'],
    choose: 'Выбрать JPG, PNG или WebP',
    drop: 'Перетащите до 4 изображений',
    limits: 'До 20 MiB и 24 MP на файл, до 48 MP суммарно.',
    sources: 'Исходники',
    remove: 'Удалить',
    focal: 'Общий фокус исходника',
    focalHint:
      'Нажмите на изображение или используйте стрелки. Shift + стрелка перемещает фокус быстрее.',
    presets: 'Размеры',
    selected: 'выбрано',
    official: 'Официальный факт',
    recommendation: 'Рекомендация imgify',
    derived: 'Производная зона',
    noSafeZone: 'Платформа может закрыть или дополнительно обрезать края.',
    freshness: 'Источники размеров проверены 25 июля 2026 года; владелец продукта перепроверяет их ежеквартально.',
    outputPan: 'Панорама этого результата',
    upscale: 'Исходник меньше нужного crop: увеличим полотно без обещания восстановить детали.',
    platformLimit: 'Лимит платформы',
    generate: 'Создать файлы и ZIP',
    generating: 'Готовим локально',
    cancel: 'Отменить',
    reset: 'Начать заново',
    progress: 'готово',
    downloadZip: 'Скачать ZIP',
    downloadOne: 'Скачать JPG',
    bytes: 'Размер файла',
    partial:
      'Часть результатов не создана. Готовые файлы сохранены — их можно скачать отдельно.',
    zipLimit:
      'ZIP превысил 64 MiB. Готовые изображения доступны по отдельности; уменьшите выбор для одного архива.',
    privacy:
      'Файлы, имена, размеры и координаты не отправляются на сервер и не попадают в аналитику. Сторонняя платформа может повторно сжать или обрезать экспорт.',
    unsupported: 'Поддерживаются только статические JPG, PNG и WebP.',
    tooLarge: 'Файл превышает лимит 20 MiB.',
    dimensions: 'Изображение превышает 24 MP / 8192 px или имеет неверный контейнер.',
    aggregate: 'Суммарное разрешение исходников превышает 48 MP.',
    tooMany: 'Можно добавить не больше четырёх исходников.',
    needSelection: 'Добавьте хотя бы один исходник и выберите хотя бы один размер.',
    browser: 'Нужен браузер с Worker и OffscreenCanvas.',
    failed: 'Не удалось создать результат.',
    cancelled: 'Генерация отменена; устаревшие результаты отброшены.',
    howToTitle: 'Как подготовить изображения',
    howTo: [
      'Добавьте от одного до четырёх локальных JPG, PNG или WebP.',
      'Укажите фокус каждого исходника и при необходимости сдвиньте отдельный crop.',
      'Выберите размеры, создайте результаты последовательно и скачайте файлы или ZIP.',
    ],
    faqTitle: 'Вопросы о размерах',
    faq: [
      {
        q: 'Изображения загружаются на сервер?',
        a: 'Нет. Проверка, decode, crop, resize и ZIP выполняются локально в браузере.',
      },
      {
        q: 'Все размеры официальные?',
        a: 'Нет. Интерфейс отдельно отмечает официальные требования, продуктовые рекомендации и производные safe-zone.',
      },
      {
        q: 'Гарантирует ли сервис вид после публикации?',
        a: 'Нет. Imgify гарантирует только экспортированное полотно и его размер; платформа может обработать файл повторно.',
      },
    ],
  },
  en: {
    eyebrow: 'Social image sizes',
    title: 'Prepare images for social media',
    description:
      'Add up to four images, review focal crops for six canvases, and download individual JPG files or one ZIP. Processing stays in your browser.',
    steps: ['1. Files', '2. Focus', '3. Sizes', '4. Export'],
    choose: 'Choose JPG, PNG or WebP',
    drop: 'Drop up to 4 images',
    limits: 'Up to 20 MiB and 24 MP per file, 48 MP total.',
    sources: 'Sources',
    remove: 'Remove',
    focal: 'Source focal point',
    focalHint: 'Click the image or use arrow keys. Shift + arrow moves faster.',
    presets: 'Sizes',
    selected: 'selected',
    official: 'Official fact',
    recommendation: 'Imgify recommendation',
    derived: 'Derived zone',
    noSafeZone: 'Platform chrome may cover or crop edges.',
    freshness: 'Preset sources verified July 25, 2026 and reviewed quarterly by the product owner.',
    outputPan: 'Pan this output',
    upscale: 'The source crop is smaller than the canvas; scaling cannot restore detail.',
    platformLimit: 'Platform limit',
    generate: 'Create files and ZIP',
    generating: 'Processing locally',
    cancel: 'Cancel',
    reset: 'Start over',
    progress: 'ready',
    downloadZip: 'Download ZIP',
    downloadOne: 'Download JPG',
    bytes: 'File size',
    partial: 'Some outputs failed. Completed files remain available individually.',
    zipLimit: 'ZIP exceeded 64 MiB. Download completed images separately or select fewer sizes.',
    privacy:
      'Files, names, dimensions and crop coordinates are never sent to a server or analytics. A third-party platform may re-encode or crop exports.',
    unsupported: 'Only static JPG, PNG and WebP are supported.',
    tooLarge: 'A file exceeds 20 MiB.',
    dimensions: 'An image exceeds 24 MP / 8192 px or has an invalid container.',
    aggregate: 'Source images exceed the 48 MP aggregate limit.',
    tooMany: 'You can add no more than four source images.',
    needSelection: 'Add a source and select at least one size.',
    browser: 'A browser with Worker and OffscreenCanvas is required.',
    failed: 'Could not create this output.',
    cancelled: 'Generation cancelled; stale results were discarded.',
    howToTitle: 'How to prepare images',
    howTo: [
      'Add one to four local JPG, PNG or WebP files.',
      'Set each source focal point and optionally pan an individual crop.',
      'Choose sizes, generate sequentially, then download files or ZIP.',
    ],
    faqTitle: 'Size questions',
    faq: [
      { q: 'Are images uploaded?', a: 'No. Validation, decode, crop, resize and ZIP stay local.' },
      { q: 'Are all sizes official?', a: 'No. Official, recommended and derived facts are labelled separately.' },
      { q: 'Is the published appearance guaranteed?', a: 'No. Platforms may reprocess exported files.' },
    ],
  },
} as const

function formatBytes(bytes: number): string {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KiB`
  return `${(bytes / (1024 * 1024)).toFixed(2)} MiB`
}

function download(url: string, filename: string): void {
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
}

function evidenceLabel(
  preset: SocialResizePreset,
  copy: (typeof COPY)[keyof typeof COPY],
): string {
  if (preset.sourceTier === 'official') return copy.official
  if (preset.sourceTier === 'derived') return copy.derived
  return copy.recommendation
}

export function SocialMediaResizeTool() {
  const { locale } = useLocale()
  const t = COPY[locale]
  const [sources, setSources] = useState<SourceAsset[]>([])
  const [selectedSourceId, setSelectedSourceId] = useState<string | null>(null)
  const [selectedPresets, setSelectedPresets] = useState<SocialResizePresetId[]>(DEFAULT_PRESETS)
  const [overrides, setOverrides] = useState<Record<string, FocalPoint>>({})
  const [outputs, setOutputs] = useState<OutputRecord[]>([])
  const [zipResult, setZipResult] = useState<ZipResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [dropActive, setDropActive] = useState(false)
  const [mobilePresetsOpen, setMobilePresetsOpen] = useState(false)
  const [focusBounds, setFocusBounds] = useState<RenderedImageBounds | null>(null)
  const generationRef = useRef(0)
  const sourceIdRef = useRef(1)
  const sourcesRef = useRef<SourceAsset[]>([])
  const outputsRef = useRef<OutputRecord[]>([])
  const zipUrlRef = useRef<string | null>(null)
  const workerRef = useRef<SocialResizeWorkerOwner | null>(null)
  const operationRef = useRef<ToolOperation | null>(null)
  const loadGenerationRef = useRef(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const focusEditorRef = useRef<HTMLDivElement>(null)
  const mobilePresetTriggerRef = useRef<HTMLButtonElement>(null)
  const mobilePresetDialogRef = useRef<HTMLDivElement>(null)
  const mobilePresetCloseRef = useRef<HTMLButtonElement>(null)
  const mobilePresetPreviousFocusRef = useRef<HTMLElement | null>(null)

  useEffect(() => {
    sourcesRef.current = sources
  }, [sources])
  useEffect(() => {
    outputsRef.current = outputs
  }, [outputs])

  const revokeResults = useCallback(() => {
    for (const output of outputsRef.current) {
      if (output.state.status === 'ready') URL.revokeObjectURL(output.state.url)
    }
    outputsRef.current = []
    setOutputs([])
    if (zipUrlRef.current) URL.revokeObjectURL(zipUrlRef.current)
    zipUrlRef.current = null
    setZipResult(null)
  }, [])

  const invalidate = useCallback((trackCancellation: boolean) => {
    generationRef.current += 1
    workerRef.current?.cancel()
    workerRef.current = null
    if (trackCancellation) operationRef.current?.error('cancelled')
    operationRef.current = null
    setBusy(false)
  }, [])

  useEffect(() => () => {
    loadGenerationRef.current += 1
    invalidate(false)
    for (const source of sourcesRef.current) URL.revokeObjectURL(source.url)
    for (const output of outputsRef.current) {
      if (output.state.status === 'ready') URL.revokeObjectURL(output.state.url)
    }
    if (zipUrlRef.current) URL.revokeObjectURL(zipUrlRef.current)
  }, [invalidate])

  const activeSource = sources.find((source) => source.id === selectedSourceId) ?? sources[0] ?? null

  useEffect(() => {
    const editor = focusEditorRef.current
    if (!editor || !activeSource) {
      setFocusBounds(null)
      return
    }
    const update = () => {
      try {
        setFocusBounds(containedImageBounds(
          editor.clientWidth,
          editor.clientHeight,
          activeSource.meta.width,
          activeSource.meta.height,
        ))
      } catch {
        setFocusBounds(null)
      }
    }
    update()
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(update)
    observer?.observe(editor)
    window.addEventListener('resize', update)
    return () => {
      observer?.disconnect()
      window.removeEventListener('resize', update)
    }
  }, [activeSource])

  const closeMobilePresets = useCallback(() => {
    setMobilePresetsOpen(false)
    requestAnimationFrame(() => {
      ;(mobilePresetPreviousFocusRef.current ?? mobilePresetTriggerRef.current)?.focus()
    })
  }, [])

  const openMobilePresets = useCallback(() => {
    mobilePresetPreviousFocusRef.current = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : mobilePresetTriggerRef.current
    setMobilePresetsOpen(true)
  }, [])

  useEffect(() => {
    if (!mobilePresetsOpen) return
    const dialog = mobilePresetDialogRef.current
    if (!dialog) return
    const frame = requestAnimationFrame(() => mobilePresetCloseRef.current?.focus())
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        closeMobilePresets()
        return
      }
      if (event.key !== 'Tab') return
      const focusable = Array.from(dialog.querySelectorAll<HTMLElement>(
        'button:not([disabled]), a[href], input:not([disabled]), [tabindex]:not([tabindex="-1"])',
      )).filter((element) => !element.hasAttribute('hidden'))
      if (focusable.length === 0) {
        event.preventDefault()
        dialog.focus()
        return
      }
      const first = focusable[0]!
      const last = focusable.at(-1)!
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => {
      cancelAnimationFrame(frame)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [closeMobilePresets, mobilePresetsOpen])
  const jobs = useMemo(
    () => buildOutputJobs(
      sources.map((source) => ({
        id: source.id,
        name: source.file.name,
        focal: source.focal,
      })),
      selectedPresets,
    ).map((job) => ({
      ...job,
      focal: overrides[job.id] ?? job.focal,
    })),
    [overrides, selectedPresets, sources],
  )
  const readyCount = outputs.filter((output) => output.state.status === 'ready').length

  const reject = useCallback((message: string, code: 'invalid_input' | 'unsupported_input' | 'size_limit') => {
    setError(message)
    trackToolRejection(ANALYTICS, code)
  }, [])

  const addFiles = useCallback(async (incoming: FileList | File[]) => {
    const files = Array.from(incoming)
    setError(null)
    if (sourcesRef.current.length + files.length > SOCIAL_RESIZE_LIMITS.maxSources) {
      reject(t.tooMany, 'size_limit')
      return
    }
    invalidate(busy)
    const loadGeneration = ++loadGenerationRef.current
    revokeResults()
    const preflighted: Array<{ file: File; meta: ImageMeta }> = []
    for (const file of files) {
      try {
        const meta = await preflightRasterFile(file, PREFLIGHT_LIMITS)
        if (loadGenerationRef.current !== loadGeneration) {
          return
        }
        preflighted.push({ file, meta })
      } catch (failure) {
        if (loadGenerationRef.current !== loadGeneration) {
          return
        }
        const code = failure instanceof FilePreflightError ? failure.code : 'decode_failed'
        if (code === 'file_too_large') reject(t.tooLarge, 'size_limit')
        else if (code === 'unsupported' || code === 'animated_unsupported') {
          reject(t.unsupported, 'unsupported_input')
        } else {
          reject(t.dimensions, code === 'too_many_pixels' ? 'size_limit' : 'invalid_input')
        }
      }
    }
    const aggregate = [
      ...sourcesRef.current.map((source) => source.meta),
      ...preflighted.map((candidate) => candidate.meta),
    ].reduce((sum, meta) => sum + meta.width * meta.height, 0)
    if (aggregate > SOCIAL_RESIZE_LIMITS.maxAggregatePixels) {
      reject(t.aggregate, 'size_limit')
      return
    }
    const accepted: SourceAsset[] = []
    for (const candidate of preflighted) {
      try {
        await verifyRasterDecode(candidate.file, candidate.meta)
        if (loadGenerationRef.current !== loadGeneration) {
          for (const source of accepted) URL.revokeObjectURL(source.url)
          return
        }
        accepted.push({
          id: `image-${sourceIdRef.current++}`,
          file: candidate.file,
          url: URL.createObjectURL(candidate.file),
          meta: candidate.meta,
          focal: { x: 0.5, y: 0.5 },
        })
      } catch {
        if (loadGenerationRef.current !== loadGeneration) {
          for (const source of accepted) URL.revokeObjectURL(source.url)
          return
        }
        reject(t.dimensions, 'invalid_input')
      }
    }
    const next = [...sourcesRef.current, ...accepted]
    if (accepted.length > 0) {
      setSources(next)
      setSelectedSourceId((current) => current ?? accepted[0]?.id ?? null)
    }
  }, [busy, invalidate, reject, revokeResults, t])

  const updateSourceFocal = useCallback((focal: FocalPoint) => {
    if (!activeSource || busy) return
    const bounded = clampOutputPan(focal)
    setSources((current) => current.map((source) =>
      source.id === activeSource.id ? { ...source, focal: bounded } : source,
    ))
    setOverrides((current) => Object.fromEntries(
      Object.entries(current).filter(([id]) => !id.startsWith(`${activeSource.id}:`)),
    ))
    revokeResults()
  }, [activeSource, busy, revokeResults])

  const removeSource = useCallback((id: string) => {
    loadGenerationRef.current += 1
    invalidate(busy)
    const target = sourcesRef.current.find((source) => source.id === id)
    if (target) URL.revokeObjectURL(target.url)
    const next = sourcesRef.current.filter((source) => source.id !== id)
    setSources(next)
    setSelectedSourceId((current) => current === id ? next[0]?.id ?? null : current)
    setOverrides((current) => Object.fromEntries(
      Object.entries(current).filter(([key]) => !key.startsWith(`${id}:`)),
    ))
    revokeResults()
  }, [busy, invalidate, revokeResults])

  const togglePreset = useCallback((id: SocialResizePresetId) => {
    if (busy) return
    setSelectedPresets((current) => current.includes(id)
      ? current.filter((value) => value !== id)
      : [...current, id].slice(0, SOCIAL_RESIZE_LIMITS.maxPresets))
    revokeResults()
  }, [busy, revokeResults])

  const changeOverride = useCallback((jobId: string, axis: keyof FocalPoint, value: number) => {
    const job = jobs.find((candidate) => candidate.id === jobId)
    if (!job || busy) return
    setOverrides((current) => ({
      ...current,
      [jobId]: clampOutputPan({
        ...(current[jobId] ?? job.focal),
        [axis]: value,
      }),
    }))
    revokeResults()
  }, [busy, jobs, revokeResults])

  const cancel = useCallback(() => {
    if (!busy) return
    invalidate(true)
    setError(t.cancelled)
  }, [busy, invalidate, t.cancelled])

  const generate = useCallback(async () => {
    loadGenerationRef.current += 1
    setError(null)
    if (typeof Worker === 'undefined' || typeof OffscreenCanvas === 'undefined') {
      reject(t.browser, 'unsupported_input')
      return
    }
    try {
      validateSocialResizeSelection(
        sources.map((source) => ({ id: source.id, ...source.meta })),
        selectedPresets,
      )
    } catch (failure) {
      reject(
        failure instanceof SocialResizeLimitError ? t.needSelection : t.failed,
        failure instanceof SocialResizeLimitError ? 'size_limit' : 'invalid_input',
      )
      return
    }

    invalidate(busy)
    revokeResults()
    const generation = generationRef.current
    const operation = startToolOperation(ANALYTICS)
    operationRef.current = operation
    const startedAt = performance.now()
    setBusy(true)
    const initial = jobs.map((job): OutputRecord => ({
      id: job.id,
      sourceId: job.sourceId,
      presetId: job.presetId,
      focal: job.focal,
      state: { status: 'pending' },
    }))
    outputsRef.current = initial
    setOutputs(initial)

    let failures = 0
    let timedOut = false
    let outputBytes = 0
    let zipLimitReached = false
    const ready: Array<{ blob: Blob; filename: string }> = []
    for (const job of jobs) {
      if (generationRef.current !== generation) return
      setOutputs((current) => current.map((output) =>
        output.id === job.id ? { ...output, state: { status: 'processing' } } : output,
      ))
      const source = sources.find((candidate) => candidate.id === job.sourceId)
      if (!source) {
        failures += 1
        continue
      }
      const preset = socialResizePreset(job.presetId)
      const filename = outputFilename(source.file.name, preset.id, 'jpg', source.id)
      const owner = runSocialResizeWorker({
        generation,
        jobId: job.id,
        file: source.file,
        presetId: preset.id,
        focal: job.focal,
        mime: 'image/jpeg',
        quality: 0.9,
      })
      workerRef.current = owner
      const response = await owner.promise
      if (generationRef.current !== generation) return
      workerRef.current = null
      if (!response.ok) {
        failures += 1
        if (response.code === 'timeout') timedOut = true
        setOutputs((current) => current.map((output) =>
          output.id === job.id
            ? { ...output, state: { status: 'error', message: t.failed } }
            : output,
        ))
        continue
      }
      const url = URL.createObjectURL(response.blob)
      ready.push({ blob: response.blob, filename })
      outputBytes += response.blob.size
      setOutputs((current) => {
        const next = current.map((output) =>
          output.id === job.id
            ? { ...output, state: { status: 'ready', blob: response.blob, url, filename } } as OutputRecord
            : output,
        )
        outputsRef.current = next
        return next
      })
      if (outputBytes > SOCIAL_RESIZE_LIMITS.maxZipBytes) {
        zipLimitReached = true
        setOutputs((current) => current.map((output) =>
          output.state.status === 'pending'
            ? { ...output, state: { status: 'error', message: t.zipLimit } }
            : output,
        ))
        break
      }
    }

    if (generationRef.current !== generation) return
    try {
      const { default: JSZip } = await import('jszip')
      if (generationRef.current !== generation) return
      const zip = new JSZip()
      for (const output of ready) zip.file(output.filename, output.blob)
      const zipBlob = await zip.generateAsync({ type: 'blob', compression: 'STORE' })
      if (generationRef.current !== generation) return
      if (zipLimitReached || zipBlob.size > SOCIAL_RESIZE_LIMITS.maxZipBytes) {
        setError(t.zipLimit)
        operation.error('size_limit')
      } else if (ready.length > 0) {
        const url = URL.createObjectURL(zipBlob)
        zipUrlRef.current = url
        setZipResult({ blob: zipBlob, url })
        if (failures > 0) {
          setError(t.partial)
          operation.error(timedOut ? 'timeout' : 'processing_failed')
        } else {
          operation.success(performance.now() - startedAt)
        }
      } else {
        setError(t.failed)
        operation.error(timedOut ? 'timeout' : 'processing_failed')
      }
    } catch {
      if (generationRef.current !== generation) return
      setError(t.failed)
      operation.error('processing_failed')
    } finally {
      if (generationRef.current === generation) {
        operationRef.current = null
        setBusy(false)
      }
    }
  }, [
    busy,
    invalidate,
    jobs,
    reject,
    revokeResults,
    selectedPresets,
    sources,
    t,
  ])

  const reset = useCallback(() => {
    loadGenerationRef.current += 1
    invalidate(busy)
    for (const source of sourcesRef.current) URL.revokeObjectURL(source.url)
    sourcesRef.current = []
    setSources([])
    setSelectedSourceId(null)
    setSelectedPresets(DEFAULT_PRESETS)
    setOverrides({})
    setError(null)
    revokeResults()
  }, [busy, invalidate, revokeResults])

  const renderPresetOptions = () => (
    <div className="mt-3 space-y-2">
      {SOCIAL_RESIZE_PRESETS.map((preset) => (
        <label key={preset.id} className="flex min-h-11 cursor-pointer items-start gap-3 rounded-md border p-3">
          <input
            className="mt-1"
            type="checkbox"
            checked={selectedPresets.includes(preset.id)}
            disabled={busy}
            onChange={() => togglePreset(preset.id)}
          />
          <span className="min-w-0">
            <span className="block text-sm font-medium">{preset.platform}: {preset.surface}</span>
            <span className="block text-xs text-muted-foreground">
              {preset.width}×{preset.height} · {evidenceLabel(preset, t)}
            </span>
            <a
              href={preset.sourceUrl}
              target="_blank"
              rel="noreferrer"
              className="text-xs text-primary underline"
              onClick={(event) => event.stopPropagation()}
            >
              {locale === 'ru' ? 'Источник' : 'Source'}
            </a>
          </span>
        </label>
      ))}
    </div>
  )

  return (
    <div className="mx-auto max-w-7xl">
      <ToolHero eyebrow={t.eyebrow} title={t.title} description={t.description} />

      <ol className="mb-4 grid grid-cols-4 gap-1 text-center text-xs font-medium sm:text-sm">
        {t.steps.map((step) => <li key={step} className="rounded-md bg-muted px-1 py-3">{step}</li>)}
      </ol>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,1fr)_22rem]">
        <div className="space-y-5">
          <Card>
            <CardContent className="p-4 sm:p-6">
              <div
                className={cn(
                  'rounded-lg border-2 border-dashed p-5 text-center',
                  dropActive ? 'border-primary bg-primary/5' : 'border-border bg-muted/20',
                )}
                onDragOver={(event) => { event.preventDefault(); setDropActive(true) }}
                onDragLeave={() => setDropActive(false)}
                onDrop={(event) => {
                  event.preventDefault()
                  setDropActive(false)
                  void addFiles(event.dataTransfer.files)
                }}
              >
                <p className="hidden font-medium [@media(pointer:fine)]:block">{t.drop}</p>
                <p className="mt-1 text-sm text-muted-foreground">{t.limits}</p>
                <input
                  ref={inputRef}
                  className="sr-only"
                  type="file"
                  multiple
                  accept="image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp"
                  onChange={(event) => {
                    if (event.target.files) void addFiles(event.target.files)
                    event.target.value = ''
                  }}
                />
                <Button className="mt-4 min-h-11" onClick={() => inputRef.current?.click()} disabled={busy}>
                  {t.choose}
                </Button>
              </div>
            </CardContent>
          </Card>

          {activeSource && (
            <Card>
              <CardContent className="p-4 sm:p-6">
                <div className="mb-3 flex items-center justify-between gap-3">
                  <div>
                    <h2 className="font-semibold">{t.focal}</h2>
                    <p className="text-sm text-muted-foreground">{t.focalHint}</p>
                  </div>
                  <Button variant="outline" className="min-h-11" onClick={() => removeSource(activeSource.id)}>
                    {t.remove}
                  </Button>
                </div>
                <div
                  ref={focusEditorRef}
                  role="application"
                  aria-label={t.focal}
                  tabIndex={0}
                  className="relative min-h-[15rem] max-h-[70vh] overflow-hidden rounded-lg bg-black outline-none ring-offset-background focus-visible:ring-2 focus-visible:ring-ring lg:h-[30rem]"
                  onPointerDown={(event) => {
                    const container = event.currentTarget.getBoundingClientRect()
                    const rendered = containedImageBounds(
                      container.width,
                      container.height,
                      activeSource.meta.width,
                      activeSource.meta.height,
                    )
                    updateSourceFocal(pointFromRenderedImage(
                      event.clientX,
                      event.clientY,
                      container.left,
                      container.top,
                      rendered,
                    ))
                  }}
                  onKeyDown={(event) => {
                    const step = event.shiftKey ? 0.1 : 0.01
                    const movement = {
                      ArrowLeft: { x: -step, y: 0 },
                      ArrowRight: { x: step, y: 0 },
                      ArrowUp: { x: 0, y: -step },
                      ArrowDown: { x: 0, y: step },
                    }[event.key]
                    if (!movement) return
                    event.preventDefault()
                    updateSourceFocal({
                      x: activeSource.focal.x + movement.x,
                      y: activeSource.focal.y + movement.y,
                    })
                  }}
                >
                  <img
                    src={activeSource.url}
                    alt=""
                    className="h-full w-full object-contain"
                    draggable={false}
                  />
                  <span
                    aria-hidden="true"
                    className="pointer-events-none absolute h-5 w-5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white bg-primary shadow"
                    style={focusBounds ? {
                      left: focusBounds.left + activeSource.focal.x * focusBounds.width,
                      top: focusBounds.top + activeSource.focal.y * focusBounds.height,
                    } : { visibility: 'hidden' }}
                  />
                </div>
                <div className="mt-3 flex gap-2 overflow-x-auto">
                  {sources.map((source) => (
                    <button
                      key={source.id}
                      type="button"
                      className={cn(
                        'min-h-11 min-w-20 rounded-md border px-3 text-sm',
                        source.id === activeSource.id && 'border-primary bg-primary/5',
                      )}
                      onClick={() => setSelectedSourceId(source.id)}
                    >
                      {source.id.replace('image-', 'Фото ')}
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {jobs.length > 0 && (
            <section aria-label="Предпросмотр результатов">
              <h2 className="mb-3 text-xl font-semibold">Предпросмотр всех результатов</h2>
              <div className="grid gap-4 sm:grid-cols-2">
                {jobs.map((job) => {
                  const source = sources.find((candidate) => candidate.id === job.sourceId)
                  const preset = socialResizePreset(job.presetId)
                  const output = outputs.find((candidate) => candidate.id === job.id)
                  const outputState = output?.state
                  if (!source) return null
                  const crop = job.focal
                  const preview = coverPreviewStyle(
                    source.meta.width,
                    source.meta.height,
                    preset.width,
                    preset.height,
                    crop,
                  )
                  const upscale = source.meta.width < preset.width || source.meta.height < preset.height
                  return (
                    <Card key={job.id}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <h3 className="font-semibold">{preset.platform}: {preset.surface}</h3>
                            <p className="text-sm text-muted-foreground">{preset.width}×{preset.height}</p>
                          </div>
                          <span className="rounded bg-muted px-2 py-1 text-[11px]">
                            {evidenceLabel(preset, t)}
                          </span>
                        </div>
                        <div
                          data-output-preview={job.id}
                          className="relative mt-3 overflow-hidden rounded-md bg-black"
                          style={{ aspectRatio: `${preset.width}/${preset.height}` }}
                        >
                          <img
                            src={source.url}
                            alt=""
                            className="absolute max-w-none"
                            style={{
                              width: `${preview.widthPercent}%`,
                              height: `${preview.heightPercent}%`,
                              left: `${preview.leftPercent}%`,
                              top: `${preview.topPercent}%`,
                            }}
                          />
                          {preset.safeZone && (
                            <div
                              className="pointer-events-none absolute border-2 border-dashed border-amber-300"
                              style={{
                                left: `${preset.safeZone.x * 100}%`,
                                top: `${preset.safeZone.y * 100}%`,
                                width: `${preset.safeZone.width * 100}%`,
                                height: `${preset.safeZone.height * 100}%`,
                              }}
                            />
                          )}
                          {preset.previewMask === 'circle' && (
                            <div className="pointer-events-none absolute inset-[4%] rounded-full border-2 border-white shadow" />
                          )}
                        </div>
                        <p className="mt-2 text-xs text-muted-foreground">
                          {preset.safeZone?.note ?? (preset.previewMask ? t.recommendation : t.noSafeZone)}
                        </p>
                        <p className="mt-1 text-xs text-muted-foreground">{preset.evidenceNote}</p>
                        {upscale && <p className="mt-2 text-xs text-amber-700 dark:text-amber-300">{t.upscale}</p>}
                        <fieldset className="mt-3">
                          <legend className="text-xs font-medium">{t.outputPan}</legend>
                          <label className="mt-2 flex min-h-11 items-center gap-2 text-xs">
                            X
                            <input
                              aria-label={`${t.outputPan} X ${job.id}`}
                              className="w-full"
                              type="range"
                              min="0"
                              max="1"
                              step="0.01"
                              value={crop.x}
                              onChange={(event) => changeOverride(job.id, 'x', Number(event.target.value))}
                            />
                          </label>
                          <label className="flex min-h-11 items-center gap-2 text-xs">
                            Y
                            <input
                              aria-label={`${t.outputPan} Y ${job.id}`}
                              className="w-full"
                              type="range"
                              min="0"
                              max="1"
                              step="0.01"
                              value={crop.y}
                              onChange={(event) => changeOverride(job.id, 'y', Number(event.target.value))}
                            />
                          </label>
                        </fieldset>
                        {outputState?.status === 'ready' && (
                          <div className="mt-3 rounded-md bg-muted p-3 text-sm">
                            <p>{preset.width}×{preset.height} · {t.bytes}: {formatBytes(outputState.blob.size)}</p>
                            {preset.maxBytes && (
                              <p className={outputState.blob.size > preset.maxBytes ? 'text-destructive' : 'text-muted-foreground'}>
                                {t.platformLimit}: {formatBytes(preset.maxBytes)}
                              </p>
                            )}
                            <Button
                              className="mt-2 min-h-11 w-full"
                              variant="outline"
                              onClick={() => download(outputState.url, outputState.filename)}
                            >
                              {t.downloadOne}
                            </Button>
                          </div>
                        )}
                        {outputState?.status === 'error' && (
                          <p role="alert" className="mt-2 text-sm text-destructive">{outputState.message}</p>
                        )}
                      </CardContent>
                    </Card>
                  )
                })}
              </div>
            </section>
          )}
        </div>

        <aside className="min-w-0 space-y-4 lg:sticky lg:top-4 lg:self-start">
          <Button
            ref={mobilePresetTriggerRef}
            id="social-resize-mobile-preset-trigger"
            className="min-h-11 w-full lg:hidden"
            variant="outline"
            onClick={openMobilePresets}
          >
            {t.presets}: {selectedPresets.length}
          </Button>
          <Card className="hidden lg:block">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <h2 className="font-semibold">{t.presets}</h2>
                <span className="text-xs text-muted-foreground">{selectedPresets.length} {t.selected}</span>
              </div>
              {renderPresetOptions()}
              <p className="mt-3 text-xs text-muted-foreground">{t.freshness}</p>
            </CardContent>
          </Card>

          <Card className="sticky bottom-2 z-20 lg:static">
            <CardContent className="p-4">
              <p className="text-sm font-medium">
                {readyCount} {t.progress} {outputs.length || jobs.length}
              </p>
              {busy && (
                <div className="mt-2 h-2 overflow-hidden rounded-full bg-muted">
                  <div
                    className="h-full bg-primary transition-[width]"
                    style={{ width: `${outputs.length ? (readyCount / outputs.length) * 100 : 0}%` }}
                  />
                </div>
              )}
              {error && <p role="alert" className="mt-3 text-sm text-destructive">{error}</p>}
              <div className="mt-4 grid gap-2">
                {busy ? (
                  <Button className="min-h-11" variant="destructive" onClick={cancel}>{t.cancel}</Button>
                ) : (
                  <Button
                    className="min-h-11"
                    disabled={sources.length === 0 || selectedPresets.length === 0}
                    onClick={() => void generate()}
                  >
                    {t.generate}
                  </Button>
                )}
                {zipResult && (
                  <Button
                    className="min-h-11"
                    variant="outline"
                    onClick={() => download(zipResult.url, 'imgify-social-media.zip')}
                  >
                    {t.downloadZip} · {formatBytes(zipResult.blob.size)}
                  </Button>
                )}
                <Button className="min-h-11" variant="ghost" disabled={busy} onClick={reset}>{t.reset}</Button>
              </div>
              <p className="mt-4 text-xs leading-5 text-muted-foreground">{t.privacy}</p>
            </CardContent>
          </Card>
        </aside>
      </div>

      {mobilePresetsOpen && (
        <div
          ref={mobilePresetDialogRef}
          className="fixed inset-0 z-50 lg:hidden"
          role="dialog"
          aria-modal="true"
          aria-labelledby="social-resize-preset-title"
          tabIndex={-1}
        >
          <button
            type="button"
            className="absolute inset-0 bg-black/50"
            aria-label={locale === 'ru' ? 'Закрыть размеры' : 'Close sizes'}
            onClick={closeMobilePresets}
          />
          <div className="absolute inset-x-0 bottom-0 max-h-[75vh] overflow-y-auto rounded-t-2xl border bg-background p-4 shadow-2xl">
            <div className="flex items-center justify-between gap-3">
              <h2 id="social-resize-preset-title" className="font-semibold">{t.presets}</h2>
              <Button
                ref={mobilePresetCloseRef}
                className="min-h-11"
                variant="outline"
                onClick={closeMobilePresets}
              >
                {locale === 'ru' ? 'Готово' : 'Done'}
              </Button>
            </div>
            {renderPresetOptions()}
            <p className="mt-3 text-xs text-muted-foreground">{t.freshness}</p>
          </div>
        </div>
      )}

      <section className="mt-10 grid gap-6 md:grid-cols-2">
        <div>
          <h2 className="text-xl font-semibold">{t.howToTitle}</h2>
          <ol className="mt-3 list-decimal space-y-2 pl-5 text-muted-foreground">
            {t.howTo.map((step) => <li key={step}>{step}</li>)}
          </ol>
        </div>
        <div>
          <h2 className="text-xl font-semibold">{t.faqTitle}</h2>
          <div className="mt-3 space-y-3">
            {t.faq.map((item) => (
              <details key={item.q} className="rounded-lg border p-4">
                <summary className="cursor-pointer font-medium">{item.q}</summary>
                <p className="mt-2 text-sm text-muted-foreground">{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>
      <section className="mt-8 border-t pt-6">
        <h2 className="text-lg font-semibold">
          {locale === 'ru' ? 'Смежные задачи' : 'Related tasks'}
        </h2>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <a href="/obrezat-foto" className="rounded-lg border p-4 hover:bg-muted/50">
            <span className="font-medium">
              {locale === 'ru' ? 'Обрезать один результат вручную' : 'Crop one result manually'}
            </span>
            <span className="mt-1 block text-sm text-muted-foreground">
              {locale === 'ru'
                ? 'Свободная рамка для одного изображения без набора социальных размеров.'
                : 'A free crop frame for one image without a social-size batch.'}
            </span>
          </a>
          <a href="/izmenit-razmer-foto" className="rounded-lg border p-4 hover:bg-muted/50">
            <span className="font-medium">
              {locale === 'ru' ? 'Задать один точный размер' : 'Set one exact size'}
            </span>
            <span className="mt-1 block text-sm text-muted-foreground">
              {locale === 'ru'
                ? 'Ручная ширина и высота для одного результата вместо пресетов.'
                : 'Manual width and height for one output instead of presets.'}
            </span>
          </a>
        </div>
      </section>
    </div>
  )
}
